
import { Investor } from "@/types/investor";
import { enrichInvestorData } from "@/utils/ai/enrichmentUtils";
import { supabase } from "@/integrations/supabase/client";
import { markStateFresh } from "./processingState";

/**
 * Process and enrich a single investor using AI
 * Simplified version to reduce complexity and make it more reliable
 */
export const enrichSingleInvestor = async (
  investor: Investor,
  relevanceContext: string,
  apiKey: string,
  callback?: ((enrichedInvestor: Investor) => Promise<boolean>) | null
): Promise<Investor> => {
  // Validate inputs
  if (!investor || !investor.id) {
    console.error("Invalid investor object provided to enrichSingleInvestor");
    throw new Error("Invalid investor object");
  }

  if (!relevanceContext) {
    console.warn("No relevance context provided for enrichment");
  }

  if (!apiKey) {
    console.error("No API key provided for enrichment");
    throw new Error("API key is required for enrichment");
  }

  try {
    console.log(`Starting enrichment process for investor: ${investor.firm || investor.name || investor.id}`);
    
    // Execute the enrichment
    const startTime = Date.now();
    const enrichedInvestor = await enrichInvestorData(investor, relevanceContext, apiKey);
    const enrichmentTime = Date.now() - startTime;
    
    console.log(`Investor ${investor.id} processed with ${enrichedInvestor.enrichedWith || 'unknown'} in ${enrichmentTime}ms`);

    // BLOCKING CALL - Update this investor in the Supabase database
    const updateResult = await updateInvestorInDatabase(enrichedInvestor);
    if (!updateResult) {
      console.error(`Failed to update investor ${investor.id} in database`);
    } else {
      console.log(`Successfully updated investor ${investor.id} in database`);
    }
    
    // Mark data as fresh to prevent stale state detection
    markStateFresh();
    
    // If a callback is provided, call it with the enriched investor
    if (callback) {
      console.log(`Running callback for investor ${investor.id}`);
      try {
        const callbackStartTime = Date.now();
        // BLOCKING CALL - Wait for callback completion
        const callbackResult = await callback(enrichedInvestor);
        console.log(`Callback for investor ${investor.id} complete in ${Date.now() - callbackStartTime}ms, result: ${callbackResult}`);
      } catch (callbackError) {
        console.error(`Error in callback for investor ${investor.id}:`, callbackError);
        // Don't throw the error here, as we still want to return the enriched investor
      }
    }
    
    return enrichedInvestor;
  } catch (error) {
    console.error(`Error processing investor ${investor.id}:`, error);
    // Preserve original investor data on error
    throw error;
  }
};

/**
 * Update a single investor in the Supabase database - BLOCKING operation
 * Updated to use the new investors table directly instead of the legacy table
 */
async function updateInvestorInDatabase(enrichedInvestor: Investor): Promise<boolean> {
  try {
    // Check if we're authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.log("Cannot update investor in database - not authenticated");
      return false;
    }

    console.log(`Updating investor ${enrichedInvestor.id} in the new investors table...`);
    
    // Transform investor to DB format
    const dbInvestor = {
      original_id: enrichedInvestor.id,
      name: enrichedInvestor.name,
      firm: enrichedInvestor.firm,
      relevance_score: enrichedInvestor.relevanceScore,
      keywords: enrichedInvestor.keywords,
      portfolio_companies: enrichedInvestor.portfolioCompanies,
      investment_focus: enrichedInvestor.investmentFocus,
      website: enrichedInvestor.website,
      matched_contact_ids: enrichedInvestor.matchedContactIds,
      last_relevance_update: enrichedInvestor.lastRelevanceUpdate,
      confidence: enrichedInvestor.confidence,
      notes: enrichedInvestor.notes,
      sources: enrichedInvestor.sources,
      enriched_with: enrichedInvestor.enrichedWith,
      enrichment_failed: enrichedInvestor.enrichmentFailed,
      location: enrichedInvestor.location,
      user_id: session.user.id
    };
    
    // Update directly in the new investors table - BLOCKING OPERATION
    const { error } = await supabase
      .from('investors')
      .upsert(dbInvestor, {
        onConflict: 'original_id,user_id'
      });
    
    if (error) {
      console.error("Failed to update investor in database:", error);
      return false;
    }
    
    console.log(`Successfully updated investor ${enrichedInvestor.id} in the investors table`);
    
    // Mark database state as fresh to prevent unnecessary reloads
    markStateFresh();
    
    return true;
    
  } catch (error) {
    console.error("Error updating investor in database:", error);
    return false;
  }
}
